import { useEffect, useRef } from 'react';

/**
 * Section highlighting for proposal preview synchronization
 *
 * Hybrid approach:
 * - Reads ?section={id} from URL (survives reloads, used when data changes)
 * - Listens for postMessage from Fiesta admin panel (instant navigation)
 * - Sends a ready signal back to parent when listener is initialized
 *
 * If section is visible: highlights without scrolling.
 * If not visible: scrolls to center then highlights.
 * Highlight fades after 2 seconds.
 *
 * @param {boolean} isEmbedMode - Only works when true (preview iframe mode)
 */
const useSectionHighlight = (isEmbedMode) => {
  const highlightTimeoutRef = useRef(null);
  const previousSectionRef = useRef(null); // Tracks postMessage highlights
  const urlSectionRef = useRef(null); // Tracks URL-based highlights

  useEffect(() => {
    if (!isEmbedMode) {
      return;
    }

    const isElementInViewport = (element) => {
      const rect = element.getBoundingClientRect();
      const windowHeight = window.innerHeight || document.documentElement.clientHeight;
      const windowWidth = window.innerWidth || document.documentElement.clientWidth;

      return rect.top >= 0 && rect.left >= 0 && rect.bottom <= windowHeight && rect.right <= windowWidth;
    };

    const applyHighlight = (element) => {
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
      }

      element.classList.add('highlight-active');

      highlightTimeoutRef.current = setTimeout(() => {
        element.classList.remove('highlight-active');
      }, 2000);
    };

    const clearAllHighlights = () => {
      const highlightedElements = document.querySelectorAll('.highlight-active');
      highlightedElements.forEach((el) => {
        el.classList.remove('highlight-active');
      });
    };

    const getFirstEnabledChild = (parentSectionId) => {
      const childSections = {
        modes: ['modes-photo', 'modes-boomerang', 'modes-video', 'modes-gif'],
        effects: ['effects-posetips', 'effects-masks', 'effects-glam', 'effects-greenscreen'],
        sharing: ['sharing', 'sharing-email', 'sharing-sms', 'sharing-print'],
      };

      const children = childSections[parentSectionId];
      if (!children) {
        return null;
      }

      // Find first child that exists in the DOM
      const firstChild = children.find((childId) => document.querySelector(`[data-section="${childId}"]`));
      return firstChild || null;
    };

    const highlightSection = (sectionId) => {
      let targetSectionId = sectionId;

      // Map parent sections to first enabled child
      if (['modes', 'effects', 'sharing'].includes(sectionId)) {
        const firstChild = getFirstEnabledChild(sectionId);
        if (!firstChild) {
          return;
        }
        targetSectionId = firstChild;
      }

      const element = document.querySelector(`[data-section="${targetSectionId}"]`);

      if (!element) {
        return;
      }

      clearAllHighlights();

      const isVisible = isElementInViewport(element);

      if (!isVisible) {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });

        setTimeout(() => {
          applyHighlight(element);
        }, 300);
      } else {
        applyHighlight(element);
      }
    };

    // URL-based highlighting with retry mechanism (for reloads)
    const checkForSectionHighlight = (retryCount = 0, maxRetries = 10) => {
      const urlParams = new URLSearchParams(window.location.search);
      const targetSection = urlParams.get('section');

      if (!targetSection) {
        if (urlSectionRef.current !== null) {
          urlSectionRef.current = null;
        }
        return;
      }

      // Deduplicate: don't re-highlight if already highlighted from URL
      if (targetSection === urlSectionRef.current) {
        return;
      }

      const element = document.querySelector(`[data-section="${targetSection}"]`);

      if (element) {
        urlSectionRef.current = targetSection;
        highlightSection(targetSection, 'url');
      } else if (retryCount < maxRetries) {
        const delay = Math.min(50 * 1.5 ** retryCount, 1000);
        setTimeout(() => {
          checkForSectionHighlight(retryCount + 1, maxRetries);
        }, delay);
      }
    };

    const handleMessage = (event) => {
      if (event.data?.type === 'PROPOSAL_HIGHLIGHT_SECTION') {
        const { sectionId } = event.data;

        // Deduplicate: don't re-highlight if already highlighted from postMessage or URL
        if (sectionId && sectionId !== previousSectionRef.current && sectionId !== urlSectionRef.current) {
          previousSectionRef.current = sectionId;
          highlightSection(sectionId, 'postMessage');
        }
      }
    };

    checkForSectionHighlight();

    window.addEventListener('popstate', checkForSectionHighlight);

    window.addEventListener('message', handleMessage);

    // Send ready message to parent (supports both web iframe and React Native WebView)
    if (window.ReactNativeWebView) {
      // React Native WebView
      window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'PROPOSAL_IFRAME_READY' }));
    } else if (window.parent !== window) {
      // Web iframe
      window.parent.postMessage({ type: 'PROPOSAL_IFRAME_READY' }, '*');
    }

    return () => {
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
      }
      window.removeEventListener('popstate', checkForSectionHighlight);
      window.removeEventListener('message', handleMessage);
    };
  }, [isEmbedMode]);
};

export default useSectionHighlight;
